import { employmentTypeToLabel, shiftTypeToLabel } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { env } from "@/lib/env";
import { CompensationType, Job, TransformedCandidateDocument } from "@/types";

const HOURS_PER_YEAR = 2080;

export const misc = {
  /**
   * Convert a base64 string to a Uint8Array of bytes.
   */
  base64StringToBytes: (base64: string) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  },

  /**
   * Build a shareable plain-text representation of a candidate profile.
   */
  buildClipboardTextForCandidateProfile: ({
    education,
    email,
    firstName,
    lastName,
    location,
    phone,
    professionalSummary,
    workExperience,
  }: {
    education: TransformedCandidateDocument["education"];
    email: TransformedCandidateDocument["email"];
    firstName: TransformedCandidateDocument["profile"]["firstName"];
    lastName: TransformedCandidateDocument["profile"]["lastName"];
    location: TransformedCandidateDocument["profile"]["location"];
    phone: TransformedCandidateDocument["profile"]["phone"];
    professionalSummary: TransformedCandidateDocument["professionalSummary"];
    workExperience: TransformedCandidateDocument["workExperience"];
  }): string => {
    const lines: string[] = [];

    // Full Name
    const fullName = [firstName ?? "", lastName ?? ""]
      .filter(Boolean)
      .join(" ");
    if (fullName) lines.push(fullName);

    // Location
    if (location) {
      lines.push(location);
    }

    // Phone
    if (phone) {
      lines.push(phone);
    }

    // Email
    if (email) {
      lines.push(email);
    }

    // Professional Summary
    if (professionalSummary) {
      // Add a blank line before the professional summary if there are other lines before it.
      if (lines.length > 0) {
        lines.push("");
      }
      lines.push("## Summary ##");
      lines.push(professionalSummary);
    }

    // Work Experience
    if (workExperience.length > 0) {
      // Add a blank line before the work experience(s) if there are other lines before it.
      if (lines.length > 0) {
        lines.push("");
      }
      lines.push("## Work Experience ##");
      workExperience.forEach((we, idx) => {
        const companyName = we.internalCompany?.name;
        if (companyName) lines.push(companyName);
        if (we.location) lines.push(we.location);
        if (we.title) lines.push(we.title);

        const timeframe =
          dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience({
            startMonth: we.startMonth ?? undefined,
            startYear: we.startYear ?? undefined,
            endMonth: we.endMonth ?? undefined,
            endYear: we.endYear ?? undefined,
            current: we.currentPosition ?? undefined,
          });
        if (timeframe) lines.push(timeframe);

        if (we.descriptions) {
          we.descriptions.forEach((d) => {
            const trimmed = d.trim();
            if (trimmed) lines.push(`- ${trimmed}`);
          });
        }

        if (idx < workExperience.length - 1) {
          lines.push("--");
        }
      });
    }

    // Education
    if (education.length > 0) {
      // Add a blank line before the education(s) if there are other lines before it.
      if (lines.length > 0) {
        lines.push("");
      }
      lines.push("## Education ##");
      education.forEach((edu, idx) => {
        if (edu.school) lines.push(edu.school);
        if (edu.location) lines.push(edu.location);
        if (edu.degree) lines.push(edu.degree);

        const timeframe =
          dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience({
            startMonth: edu.startMonth ?? undefined,
            startYear: edu.startYear ?? undefined,
            endMonth: edu.endMonth ?? undefined,
            endYear: edu.endYear ?? undefined,
            current: edu.current ?? undefined,
          });
        if (timeframe) lines.push(timeframe);

        if (idx < education.length - 1) {
          lines.push("--");
        }
      });
    }

    return lines.join("\n");
  },

  /**
   * Build a shareable plain-text representation of a transcript (staffer call or voice AI outreach).
   */
  buildClipboardTextForTranscript: ({
    getRoleLabel,
    transcriptLines,
  }: {
    getRoleLabel: (role: string) => string;
    transcriptLines: { content: string; role: string; time: string }[];
  }): string => {
    return transcriptLines
      .map(
        (line) => `${line.time} - ${getRoleLabel(line.role)}: ${line.content}`,
      )
      .join("\n");
  },

  /**
   * Format a time string (HH:MM format) to 12-hour format with AM/PM.
   * Returns empty string if input is null or empty.
   */
  formatShiftTime: (time: string | null): string => {
    if (!time) return "";

    const [hoursStr, minutesStr] = time.split(":");
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) return time;

    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    return `${String(hours12)}:${minutesStr.padStart(2, "0")} ${period}`;
  },

  /**
   * Convert TipTap HTML rich text to plain text with bullet points.
   * - Paragraphs become newlines
   * - List items become bullet points with "• " prefix
   * - All other HTML tags are stripped
   */
  htmlToPlainText: (html: string | null): string => {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const lines: string[] = [];

    const processNode = (node: Node): void => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();

        if (tagName === "li") {
          const text = element.textContent?.trim();
          if (text) lines.push(`• ${text}`);
        } else if (tagName === "p") {
          const text = element.textContent?.trim();
          if (text) lines.push(text);
        } else if (tagName === "ul" || tagName === "ol") {
          // Process list items
          Array.from(element.children).forEach(processNode);
        } else {
          // For other elements, process children
          Array.from(element.childNodes).forEach(processNode);
        }
      }
    };

    Array.from(doc.body.childNodes).forEach(processNode);

    return lines.join("\n");
  },

  /**
   * Build a shareable plain-text representation of a job profile.
   */
  buildClipboardTextForJobProfile: ({
    companyAddress,
    companyDescription,
    companyName,
    companyWebsiteUrl,
    job,
  }: {
    companyAddress: string | null;
    companyDescription: string | null;
    companyName: string;
    companyWebsiteUrl: string | null;
    job: Job;
  }): string => {
    const lines: string[] = [];

    // Company header (name with optional URL)
    const companyLine = companyWebsiteUrl
      ? `${companyName} (${companyWebsiteUrl})`
      : companyName;
    lines.push(companyLine);

    if (companyAddress) {
      lines.push(companyAddress);
    }

    // About section
    const aboutText = misc.htmlToPlainText(companyDescription);
    if (aboutText) {
      lines.push("");
      lines.push("About:");
      lines.push(aboutText);
    }

    // Job Details section
    lines.push("");
    lines.push("Job Details:");
    lines.push(`Role: ${job.title}`);

    const jobAddressParts = [
      job.address,
      job.city,
      job.state,
      job.zipCode,
    ].filter(Boolean);
    if (jobAddressParts.length > 0) {
      lines.push(`Location: ${jobAddressParts.join(", ")}`);
    }

    lines.push(`Type: ${employmentTypeToLabel[job.employmentType]}`);

    const compensationUnit =
      job.compensationType === CompensationType.HOURLY ? "/ hr" : "/ yr";
    lines.push(
      `Pay: $${String(job.compensationMin)} - $${String(job.compensationMax)} ${compensationUnit}`,
    );

    // Shift Differential (if available)
    if (job.shiftDifferentialMin != null && job.shiftDifferentialMax != null) {
      lines.push(
        `Shift Differential: $${String(job.shiftDifferentialMin)} - $${String(job.shiftDifferentialMax)} / hr`,
      );
    }

    // Shift Types (if available)
    if (job.shiftType.length > 0) {
      const shiftLabels = job.shiftType
        .map((st) => shiftTypeToLabel[st])
        .join(", ");
      lines.push(`Shifts: ${shiftLabels}`);
    }

    // Shift Times (if available)
    if (job.shiftStart && job.shiftEnd) {
      const startFormatted = misc.formatShiftTime(job.shiftStart);
      const endFormatted = misc.formatShiftTime(job.shiftEnd);
      lines.push(`Hours: ${startFormatted} - ${endFormatted}`);
    }

    // Overview
    const overviewText = misc.htmlToPlainText(job.overview);
    if (overviewText) {
      lines.push("");
      lines.push("Overview:");
      lines.push(overviewText);
    }

    // Responsibilities
    const responsibilitiesText = misc.htmlToPlainText(job.responsibilities);
    if (responsibilitiesText) {
      lines.push("");
      lines.push("Responsibilities:");
      lines.push(responsibilitiesText);
    }

    // Qualifications
    const qualificationsText = misc.htmlToPlainText(job.qualifications);
    if (qualificationsText) {
      lines.push("");
      lines.push("Qualifications:");
      lines.push(qualificationsText);
    }

    // Benefits
    const benefitsText = misc.htmlToPlainText(job.benefits);
    if (benefitsText) {
      lines.push("");
      lines.push("Benefits:");
      lines.push(benefitsText);
    }

    return lines.join("\n");
  },

  /**
   * Calculate the estimated revenue amount based on the compensation type, minimum compensation, maximum compensation, and percentage of salary.
   */
  calculateEstimatedRevenueAmount: (
    isHourly: boolean,
    min: number,
    max: number,
    percent: number | null,
  ) => {
    if (!percent) return ""; // Empty string will properly clear the field.
    const estimatedRevenueAmount = isHourly
      ? (percent / 100) * ((max + min) / 2) * HOURS_PER_YEAR
      : (percent / 100) * ((max + min) / 2);

    // Must prevent floating-point arithmetic errors by rounding to 2 decimal places
    return Number(estimatedRevenueAmount.toFixed(2));
  },

  /**
   * Convert a `File` object returned by the upload input into a base 64 string.
   */
  convertFileToBase64: (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  enforceCurrencyInput: (e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    // Block if key is "." and value already contains a decimal
    if (e.key === "." && value.includes(".")) {
      e.preventDefault();
      return;
    }

    // Block digits if already at 2 decimal places
    if (/[0-9]/.test(e.key) && value.includes(".")) {
      const decimalPart = value.split(".")[1];
      if (decimalPart && decimalPart.length >= 2) {
        e.preventDefault();
        return;
      }
    }

    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Tab" &&
      e.key !== "." &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  },

  enforceNumberInput: (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Tab" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  },

  /**
   * If a pasted value is a valid phone number, remove any non-digits
   */
  formatPhoneNumberOnPaste: (pastedText: string): string => {
    const digits = pastedText.replace(/\D+/g, "");

    // Ensure pasted value is valid phone number, if so, return digits
    if (digits.length === 10 || digits.length === 11) {
      return digits;
    }

    return pastedText;
  },

  /**
   * If copied value is a phone number, format it as (123) 456-7890
   * @param copiedText - The text to format. If the copied text is a phone number, it will only contain the 11 digits
   */
  formatPhoneNumberOnCopy: (copiedText: string): string => {
    // If it’s 11 digits and starts with '1'
    if (copiedText.length === 11 && copiedText.startsWith("1")) {
      const areaCode = copiedText.slice(1, 4);
      const middleDigits = copiedText.slice(4, 7);
      const lastFourDigits = copiedText.slice(7, 11);

      return `(${areaCode}) ${middleDigits}-${lastFourDigits}`;
    }

    return copiedText;
  },

  handleFormFieldToDatabaseValue: (
    value: string | number | null,
    type?: "date" | "number",
  ): string | number | null => {
    if (!type) return value;
    if (type === "date" && typeof value === "string") {
      return dates.datetimeLocalToIso(value) ?? value;
    } else if (type === "number") {
      if (typeof value === "number") return value;
      return value !== null && value !== "" ? parseFloat(value) : null;
    } else {
      return value;
    }
  },

  /**
   * Determines if the compensation is hourly (true) or yearly (false) based on the compensation amount.
   */
  isCompensationHourly: (pay: number): boolean => {
    return pay <= 1000;
  },

  /**
   * Convert a plain object to URLSearchParams for use in a URL query string.
   */
  prepareQueryParams: (
    queryOptions: Record<
      string,
      string | string[] | number | number[] | boolean | null | undefined
    >,
  ): URLSearchParams => {
    const queryParams = new URLSearchParams();
    Object.entries(queryOptions).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      else if (Array.isArray(value)) {
        value.forEach((v) => {
          queryParams.append(key, v.toString());
        });
      } else {
        queryParams.append(key, value.toString());
      }
    });
    return queryParams;
  },

  /**
   * Converts a string to title case by capitalizing the first letter of each word (if applicable).
   */
  toTitleCase: (str: string): string => {
    if (str === "") return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  /**
   * Generate the app download URL for a given user ID.
   */
  generateAppDownloadUrl: (userId: number, stafferId: number): string => {
    return `${env.getTalentBaseUrl()}/r/download?targetId=${userId.toString()}&attributionId=${stafferId.toString()}`;
  },
};
